import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, History } from "lucide-react";
import { API, BUCKET_COLOR } from "./constants";
import { SummaryStat } from "./SummaryStat";
import { LegendDot } from "./LegendDot";

function HistoryBucket({ bucket }) {
  const { status, start, snapshot_count, missing_critical, missing_important } = bucket;
  // Build a tooltip-friendly title because shadcn Tooltip would be overkill here.
  const date = new Date(start);
  const tip = [
    date.toLocaleString("es-MX"),
    `Estado: ${status}`,
    snapshot_count > 0 ? `Snapshots: ${snapshot_count}` : "Sin datos",
    missing_critical?.length ? `Críticos: ${missing_critical.join(", ")}` : null,
    missing_important?.length ? `Importantes: ${missing_important.join(", ")}` : null,
  ].filter(Boolean).join("\n");
  return (
    <div
      data-testid={`history-bucket-${status}`}
      title={tip}
      className={`flex-1 min-w-[2px] h-full rounded-[1px] hover:opacity-80 transition-opacity ${BUCKET_COLOR[status] || BUCKET_COLOR.unknown}`}
    />
  );
}

export function HealthHistoryCard() {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);

  const fetchHistory = useCallback(async (d) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/superadmin/health-history?days=${d}&bucket_minutes=60`,
        { withCredentials: true },
      );
      setHistory(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Error al cargar historial");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory(days);
    // Refresh every 5 min — same cadence as the snapshot scheduler.
    const interval = setInterval(() => fetchHistory(days), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [days, fetchHistory]);

  const summary = history?.summary;
  const buckets = history?.buckets || [];
  const dateLabel = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <div
      data-testid="health-history-card"
      className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-500/10 rounded-lg shrink-0">
            <History className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Historial de salud</h3>
            <p className="text-xs text-slate-500 mt-1">
              Cada barra representa 1 hora; el color es el peor estado observado en
              ese intervalo. Snapshots tomados cada 5 minutos, retención 30 días.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="history-range-selector">
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`history-range-${d}d`}
              className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                days === d
                  ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
                  : "text-slate-400 border-slate-700 hover:bg-slate-800"
              }`}
            >
              {d === 1 ? "24 h" : `${d} días`}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3">
          {error}
        </div>
      )}

      {loading && !history ? (
        <div className="h-20 flex items-center justify-center text-slate-500 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          Cargando historial…
        </div>
      ) : (
        <>
          {/* Bar */}
          <div
            data-testid="history-bar"
            className="flex items-stretch gap-[2px] h-10 bg-slate-950 rounded-md p-1 overflow-hidden"
          >
            {buckets.map((b) => (
              <HistoryBucket key={b.start} bucket={b} />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-slate-500 mt-2 font-mono">
            <span>{dateLabel(history?.from)}</span>
            <span>{dateLabel(history?.to)}</span>
          </div>

          {/* Summary stats */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
              <SummaryStat
                label="Uptime"
                value={`${summary.uptime_pct}%`}
                tone={summary.uptime_pct >= 99 ? "ok" : summary.uptime_pct >= 95 ? "warn" : "bad"}
                testid="summary-uptime"
              />
              <SummaryStat
                label="Saludables"
                value={summary.healthy}
                tone="ok"
                testid="summary-healthy"
              />
              <SummaryStat
                label="Degradados"
                value={summary.degraded}
                tone="warn"
                testid="summary-degraded"
              />
              <SummaryStat
                label="No saludables"
                value={summary.unhealthy}
                tone="bad"
                testid="summary-unhealthy"
              />
            </div>
          )}

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-3 mt-4 text-[11px] text-slate-400">
            <LegendDot color={BUCKET_COLOR.healthy} label="Saludable" />
            <LegendDot color={BUCKET_COLOR.degraded} label="Degradado" />
            <LegendDot color={BUCKET_COLOR.unhealthy} label="No saludable" />
            <LegendDot color={BUCKET_COLOR.unknown} label="Sin datos" />
          </div>
        </>
      )}
    </div>
  );
}
